/**
 * Server-side data loaders for the `/orca/*` admin routes.
 *
 * Each `beforeLoad` here calls a TanStack `createServerFn` handler that runs
 * the matching admin RPC server-side and dehydrates the result into the
 * route context's `dehydratedAtoms` array. The corresponding route atom
 * (wrapped with `hydratable`) reads that dehydrated entry at first paint,
 * so the page renders real data without waiting on a client RPC round-trip.
 *
 * `createServerFn` ensures the schema imports + RPC client construction
 * never ship to the client bundle — only the thin server-fetch wrapper does.
 *
 * @since 1.0.0
 */
import type { Hydration } from "@effect-atom/atom-react";
import { RpcClient } from "@effect/rpc";
import {
  AdminOrcaDefaultsRpcs,
  AdminOrcaEventsRpcs,
  AdminOrcaProjectRpcs,
  AdminOrcaRepoRpcs,
  AdminOrcaRunnerRpcs,
  AdminOrcaTaskRpcs,
  LoopDraftRpcs,
  LoopDraftView,
  LoopInvocationView,
  LoopViewerRpcs,
  OrcaDefaultsSnapshot,
  OrcaEventsSummary,
  OrcaProjectDetail,
  OrcaProjectSummary,
  OrcaRepoSeenRow,
  OrcaRunnerSummary,
  OrcaTaskDetail,
} from "@fm/api/rpc";
import { OrcaRepo } from "@fm/db/models/OrcaRepo";
import { OrcaTask } from "@fm/db/models/OrcaTask";
import { createServerFn } from "@tanstack/react-start";
import { getCookie, getRequestHeaders } from "@tanstack/react-start/server";
import { Effect, Schema } from "effect";
import type { BeforeLoadContext } from "./hydration.ts";
import { hydrateAtoms } from "./hydration.ts";
import { forwardedHostFromHeaders } from "./server-auth.ts";
import { makeOrcaServerRpcLayer } from "./server-rpc.ts";

export const ORCA_TASKS_KEY = "orca.tasks";
export const ORCA_PROJECTS_KEY = "orca.projects";
export const ORCA_CONTAINERS_KEY = "orca.containers";
export const ORCA_RUNNERS_KEY = "orca.runners";
export const ORCA_EVENTS_SUMMARY_KEY = "orca.events.summary";
export const ORCA_DEFAULTS_KEY = "orca.defaults";
export const ORCA_REPOS_KEY = "orca.repos";
export const ORCA_SEEN_REPOS_KEY = "orca.repos.seen";

/**
 * Per-project detail hydration key. Matches the SSR seed atom-family member
 * produced by `projectDetailSeedAtomFamily(projectId)` in
 * `lib/atoms/orca-admin-projects.ts` (the live stream takes over on mount).
 *
 * @since 1.0.0
 */
export const orcaProjectDetailKey = (projectId: string) => `orca.project.${projectId}`;

/**
 * Per-invocation hydration key for the loop viewer's invocation page (OL-258).
 * Matches the SSR seed atom-family member produced by
 * `loopInvocationSeedAtomFamily(invocationId)` in
 * `lib/atoms/loop-invocation-live.ts` — the `loops.viewer.invocationStream`
 * subscription takes over on mount, but the FIRST paint is this seed.
 *
 * @since 1.0.0
 */
export const loopInvocationKey = (invocationId: string) => `loop.invocation.${invocationId}`;

/**
 * Per-draft hydration key for the live draft canvas (OL-314). Matches the SSR
 * seed atom-family member produced by `loopDraftSeedAtomFamily(draftId)` in
 * `lib/atoms/loop-draft-live.ts` — the `loops.drafts.watch` subscription takes
 * over on mount, but the FIRST paint is this seed.
 *
 * @since 1.0.0
 */
export const loopDraftKey = (draftId: string) => `loop.draft.${draftId}`;

/**
 * Per-repo detail hydration key. Matches `repoDetailAtomFamily(slug)` in
 * `lib/atoms/orca-admin-repos.ts`.
 *
 * @since 1.0.0
 */
export const orcaRepoDetailKey = (slug: string) => `orca.repo.${slug}`;

/**
 * Per-repo resolved-contract hydration key. Matches
 * `repoResolvedContractAtomFamily(slug)` in `lib/atoms/orca-admin-repos.ts`.
 *
 * @since 1.0.0
 */
export const orcaRepoContractKey = (slug: string) => `orca.repo.contract.${slug}`;

/**
 * Per-task detail hydration key, keyed by the Linear issue identifier. Matches
 * `taskDetailAtomFamily(issueIdentifier)` in the task-detail screen module.
 *
 * @since 1.0.0
 */
export const orcaTaskDetailKey = (issueIdentifier: string) => `orca.task.${issueIdentifier}`;

/**
 * Shape we accept from TanStack Start's `beforeLoad` hook. Only the bits we
 * touch are typed; everything else is opaque.
 *
 * @since 1.0.0
 */
export interface BeforeLoadArgs extends BeforeLoadContext {
  readonly params?: Readonly<Record<string, string>>;
}

/**
 * Result returned from each `beforeLoad` — extends the parent route context
 * with the dehydrated atoms accumulated so far.
 *
 * @since 1.0.0
 */
export interface BeforeLoadResult {
  readonly dehydratedAtoms: Array<Hydration.DehydratedAtom>;
}

interface DehydrationPayload {
  readonly dehydratedAtoms: Array<Hydration.DehydratedAtom>;
}

/**
 * Resolve the Clerk bearer token for the current request, or `null` if no
 * authenticated session exists.
 *
 * Reads the `__session` cookie directly from the incoming request and
 * returns its value verbatim — Clerk's session cookie IS the JWT (see
 * `@clerk/backend`'s `Cookies.Session = "__session"` constant, plus the
 * error message at JWKS resolution time: "the __session cookie or the
 * HTTP authorization header contain a Clerk-generated session JWT").
 *
 * Previously this used `auth().getToken()` from
 * `@clerk/tanstack-react-start/server`, which round-trips to Clerk's API
 * to mint a fresh token. That call took 1–3 seconds on the first SSR
 * paint and sometimes returned `Unauthorized` mid-warmup (see PR #717
 * follow-up). Reading the cookie directly is < 1 ms and never fails when
 * the user is signed in. The API verifies the same JWT locally via
 * `verifyToken({ jwtKey })` (the networkless path) in
 * `packages/api/src/rpc/auth.middleware.ts:117`, so no API-side change
 * is required.
 *
 * @since 1.0.0
 */
const getServerToken = (): string | null => getCookie("__session") ?? null;

/**
 * The RPC layer for one SSR request: the visitor's brand host rides along, so
 * the api verifies the session with that brand's Clerk key.
 *
 * Both `AdminMiddleware` and `LoopActorMiddleware` pick the Clerk instance from
 * `X-Forwarded-Host ?? Host`. The internal hop's own `Host` is the orcaloops-api
 * cluster service name, which resolves to the default brand — so without the
 * header every orcaloops.ai session fails verification and every seed is
 * dropped. The host comes from the inbound request headers only; the api maps
 * any value to one of the Clerk instances it holds keys for and still checks
 * the signature, so a wrong host fails closed.
 */
const orcaRpcLayerForRequest = (token: string): ReturnType<typeof makeOrcaServerRpcLayer> =>
  makeOrcaServerRpcLayer(token, forwardedHostFromHeaders(getRequestHeaders()));

// ============================================================================
// Server functions — server-only. The heavy schema/RPC imports are tree-shaken
// from the client bundle because these handlers are extracted at build time
// by the TanStack Start plugin.
// ============================================================================

// oxlint-disable-next-line eslint-js/no-restricted-syntax -- TanStack Start server functions require async/await
const orcaTasksServerFn = createServerFn({ method: "GET" }).handler(async (ctx): Promise<DehydrationPayload> => {
  // (sync — reads cookie, no I/O)
  const token = getServerToken();
  if (!token) return { dehydratedAtoms: [] };
  const rpcLayer = orcaRpcLayerForRequest(token);
  // A task-list failure is left UN-seeded (null sentinel) instead of folded into
  // an empty array. Seeding `[]` on an outage would first-paint as
  // `OrcaBoardEmpty` ("no tasks in flight") and the error frame would be
  // unreachable; with no dehydrated atom the client `boardTasksAtom` fetches and
  // a persistent outage lands `OrcaBoardError`. A genuinely empty list still
  // seeds `[]` → `OrcaBoardEmpty`, so the two states stay distinguishable.
  // oxlint-disable-next-line eslint-js/no-restricted-syntax -- Effect.runPromise requires await
  const tasks = await Effect.runPromise(
    RpcClient.make(AdminOrcaTaskRpcs).pipe(
      Effect.flatMap((client) => client.admin["orcaTask.list"](void 0)),
      Effect.provide(rpcLayer),
      Effect.scoped,
      Effect.catchAll(() => Effect.succeed(null as ReadonlyArray<typeof OrcaTask.Type> | null)),
    ),
  );
  if (tasks === null) return { dehydratedAtoms: [] };
  // oxlint-disable-next-line eslint-js/no-restricted-syntax -- hydrateAtoms returns a Promise
  const hydration = await hydrateAtoms({
    [ORCA_TASKS_KEY]: {
      schema: Schema.Array(OrcaTask.json),
      effect: Effect.succeed(tasks),
    },
  })(ctx);
  return { dehydratedAtoms: hydration.dehydratedAtoms };
});

// oxlint-disable-next-line eslint-js/no-restricted-syntax -- TanStack Start server functions require async/await
const orcaProjectsServerFn = createServerFn({ method: "GET" }).handler(async (ctx): Promise<DehydrationPayload> => {
  // (sync — reads cookie, no I/O)
  const token = getServerToken();
  if (!token) return { dehydratedAtoms: [] };
  const rpcLayer = orcaRpcLayerForRequest(token);
  // A projects-list outage degrades to `null` — NOT to `[]`. Seeding `[]` would
  // first-paint an outage as the (success) EMPTY frame and make
  // `OrcaProjectListError` unreachable; leaving the atom unseeded instead lets
  // the client's own fetch run and surface its failure as the error frame.
  // oxlint-disable-next-line eslint-js/no-restricted-syntax -- Effect.runPromise requires await
  const rows = await Effect.runPromise(
    RpcClient.make(AdminOrcaProjectRpcs).pipe(
      Effect.flatMap((client) => client.admin["orcaProject.list"](void 0)),
      Effect.provide(rpcLayer),
      Effect.scoped,
      Effect.catchAll(() => Effect.succeed(null as ReadonlyArray<typeof OrcaProjectSummary.Type> | null)),
    ),
  );
  if (rows === null) return { dehydratedAtoms: [] };
  // oxlint-disable-next-line eslint-js/no-restricted-syntax -- hydrateAtoms returns a Promise
  const hydration = await hydrateAtoms({
    [ORCA_PROJECTS_KEY]: {
      schema: Schema.Array(OrcaProjectSummary),
      effect: Effect.succeed(rows),
    },
  })(ctx);
  return { dehydratedAtoms: hydration.dehydratedAtoms };
});

const orcaProjectDetailServerFn = createServerFn({ method: "GET" })
  .inputValidator((projectId: string): string => projectId)
  // oxlint-disable-next-line eslint-js/no-restricted-syntax -- TanStack Start server functions require async/await
  .handler(async (ctx): Promise<DehydrationPayload> => {
    const projectId = ctx.data;
    if (!projectId) return { dehydratedAtoms: [] };
    // (sync — reads cookie, no I/O)
    const token = getServerToken();
    if (!token) return { dehydratedAtoms: [] };
    const rpcLayer = orcaRpcLayerForRequest(token);
    // oxlint-disable-next-line eslint-js/no-restricted-syntax -- Effect.runPromise requires await
    const detail = await Effect.runPromise(
      RpcClient.make(AdminOrcaProjectRpcs).pipe(
        Effect.flatMap((client) => client.admin["orcaProject.get"]({ id: projectId })),
        Effect.provide(rpcLayer),
        Effect.scoped,
        Effect.catchAll(() => Effect.succeed(null as typeof OrcaProjectDetail.Type | null)),
      ),
    );
    if (detail === null) return { dehydratedAtoms: [] };
    // oxlint-disable-next-line eslint-js/no-restricted-syntax -- hydrateAtoms returns a Promise
    const hydration = await hydrateAtoms({
      [orcaProjectDetailKey(projectId)]: {
        schema: OrcaProjectDetail,
        effect: Effect.succeed(detail),
      },
    })(ctx);
    return { dehydratedAtoms: hydration.dehydratedAtoms };
  });

// oxlint-disable-next-line eslint-js/no-restricted-syntax -- TanStack Start server functions require async/await
const orcaRunnersServerFn = createServerFn({ method: "GET" }).handler(async (ctx): Promise<DehydrationPayload> => {
  // (sync — reads cookie, no I/O)
  const token = getServerToken();
  if (!token) return { dehydratedAtoms: [] };
  const rpcLayer = orcaRpcLayerForRequest(token);
  // A runner-list failure is left UN-seeded (null sentinel), like the task list:
  // seeding `[]` on an outage first-paints "No runners registered" while the
  // pool is live. A genuinely empty pool still seeds `[]`.
  // oxlint-disable-next-line eslint-js/no-restricted-syntax -- Effect.runPromise requires await
  const rows = await Effect.runPromise(
    RpcClient.make(AdminOrcaRunnerRpcs).pipe(
      Effect.flatMap((client) => client.admin["orcaRunner.list"](void 0)),
      Effect.provide(rpcLayer),
      Effect.scoped,
      Effect.catchAll(() => Effect.succeed(null as ReadonlyArray<typeof OrcaRunnerSummary.Type> | null)),
    ),
  );
  if (rows === null) return { dehydratedAtoms: [] };
  // oxlint-disable-next-line eslint-js/no-restricted-syntax -- hydrateAtoms returns a Promise
  const hydration = await hydrateAtoms({
    [ORCA_RUNNERS_KEY]: {
      schema: Schema.Array(OrcaRunnerSummary),
      effect: Effect.succeed(rows),
    },
  })(ctx);
  return { dehydratedAtoms: hydration.dehydratedAtoms };
});

// oxlint-disable-next-line eslint-js/no-restricted-syntax -- TanStack Start server functions require async/await
const orcaEventsServerFn = createServerFn({ method: "GET" }).handler(async (ctx): Promise<DehydrationPayload> => {
  // (sync — reads cookie, no I/O)
  const token = getServerToken();
  if (!token) return { dehydratedAtoms: [] };
  const rpcLayer = orcaRpcLayerForRequest(token);
  // oxlint-disable-next-line eslint-js/no-restricted-syntax -- Effect.runPromise requires await
  const summary = await Effect.runPromise(
    RpcClient.make(AdminOrcaEventsRpcs).pipe(
      Effect.flatMap((client) => client.admin["orcaEvents.summary"]({ sinceHours: 24 })),
      Effect.provide(rpcLayer),
      Effect.scoped,
      Effect.catchAll(() => Effect.succeed(null as typeof OrcaEventsSummary.Type | null)),
    ),
  );
  if (summary === null) return { dehydratedAtoms: [] };
  // oxlint-disable-next-line eslint-js/no-restricted-syntax -- hydrateAtoms returns a Promise
  const hydration = await hydrateAtoms({
    [ORCA_EVENTS_SUMMARY_KEY]: {
      schema: OrcaEventsSummary,
      effect: Effect.succeed(summary),
    },
  })(ctx);
  return { dehydratedAtoms: hydration.dehydratedAtoms };
});

// oxlint-disable-next-line eslint-js/no-restricted-syntax -- TanStack Start server functions require async/await
const orcaDefaultsServerFn = createServerFn({ method: "GET" }).handler(async (ctx): Promise<DehydrationPayload> => {
  // (sync — reads cookie, no I/O)
  const token = getServerToken();
  if (!token) return { dehydratedAtoms: [] };
  const rpcLayer = orcaRpcLayerForRequest(token);
  // oxlint-disable-next-line eslint-js/no-restricted-syntax -- Effect.runPromise requires await
  const snapshot = await Effect.runPromise(
    RpcClient.make(AdminOrcaDefaultsRpcs).pipe(
      Effect.flatMap((client) => client.admin["orcaDefaults.get"](void 0)),
      Effect.provide(rpcLayer),
      Effect.scoped,
      Effect.catchAll(() => Effect.succeed(null as typeof OrcaDefaultsSnapshot.Type | null)),
    ),
  );
  if (snapshot === null) return { dehydratedAtoms: [] };
  // oxlint-disable-next-line eslint-js/no-restricted-syntax -- hydrateAtoms returns a Promise
  const hydration = await hydrateAtoms({
    [ORCA_DEFAULTS_KEY]: {
      schema: OrcaDefaultsSnapshot,
      effect: Effect.succeed(snapshot),
    },
  })(ctx);
  return { dehydratedAtoms: hydration.dehydratedAtoms };
});

// oxlint-disable-next-line eslint-js/no-restricted-syntax -- TanStack Start server functions require async/await
const orcaReposServerFn = createServerFn({ method: "GET" }).handler(async (ctx): Promise<DehydrationPayload> => {
  // (sync — reads cookie, no I/O)
  const token = getServerToken();
  if (!token) return { dehydratedAtoms: [] };
  const rpcLayer = orcaRpcLayerForRequest(token);
  // Both lists come from one read and fold together: they hit the same upstream
  // with the same token, so they fail together. A failure seeds NEITHER key —
  // seeding `[]` would first-paint the empty registry while repos exist.
  // oxlint-disable-next-line eslint-js/no-restricted-syntax -- Effect.runPromise requires await
  const lists = await Effect.runPromise(
    RpcClient.make(AdminOrcaRepoRpcs).pipe(
      Effect.flatMap((client) =>
        Effect.all([client.admin["orcaRepo.list"](void 0), client.admin["orcaRepo.seen"](void 0)])
      ),
      Effect.provide(rpcLayer),
      Effect.scoped,
      Effect.catchAll(() => Effect.succeed(null)),
    ),
  );
  if (lists === null) return { dehydratedAtoms: [] };
  const [repos, seen] = lists;
  // oxlint-disable-next-line eslint-js/no-restricted-syntax -- hydrateAtoms returns a Promise
  const hydration = await hydrateAtoms({
    [ORCA_REPOS_KEY]: {
      schema: Schema.Array(OrcaRepo.json),
      effect: Effect.succeed(repos),
    },
    [ORCA_SEEN_REPOS_KEY]: {
      schema: Schema.Array(OrcaRepoSeenRow),
      effect: Effect.succeed(seen),
    },
  })(ctx);
  return { dehydratedAtoms: hydration.dehydratedAtoms };
});

const orcaRepoDetailServerFn = createServerFn({ method: "GET" })
  .inputValidator((slug: string): string => slug)
  // oxlint-disable-next-line eslint-js/no-restricted-syntax -- TanStack Start server functions require async/await
  .handler(async (ctx): Promise<DehydrationPayload> => {
    const slug = ctx.data;
    if (!slug) return { dehydratedAtoms: [] };
    // (sync — reads cookie, no I/O)
    const token = getServerToken();
    if (!token) return { dehydratedAtoms: [] };
    const rpcLayer = orcaRpcLayerForRequest(token);
    // A RecordNotFoundError (unconfigured repo) degrades to null → no dehydrated
    // atom, so the client fetch runs and lands the screens' unconfigured arm.
    // oxlint-disable-next-line eslint-js/no-restricted-syntax -- Effect.runPromise requires await
    const row = await Effect.runPromise(
      RpcClient.make(AdminOrcaRepoRpcs).pipe(
        Effect.flatMap((client) => client.admin["orcaRepo.get"]({ repo: slug })),
        Effect.provide(rpcLayer),
        Effect.scoped,
        Effect.catchAll(() => Effect.succeed(null as typeof OrcaRepo.Type | null)),
      ),
    );
    if (row === null) return { dehydratedAtoms: [] };
    // oxlint-disable-next-line eslint-js/no-restricted-syntax -- hydrateAtoms returns a Promise
    const hydration = await hydrateAtoms({
      [orcaRepoDetailKey(slug)]: {
        schema: OrcaRepo.json,
        effect: Effect.succeed(row),
      },
    })(ctx);
    return { dehydratedAtoms: hydration.dehydratedAtoms };
  });

const orcaTaskDetailServerFn = createServerFn({ method: "GET" })
  .inputValidator((issueIdentifier: string): string => issueIdentifier)
  // oxlint-disable-next-line eslint-js/no-restricted-syntax -- TanStack Start server functions require async/await
  .handler(async (ctx): Promise<DehydrationPayload> => {
    const issueIdentifier = ctx.data;
    if (!issueIdentifier) return { dehydratedAtoms: [] };
    // (sync — reads cookie, no I/O)
    const token = getServerToken();
    if (!token) return { dehydratedAtoms: [] };
    const rpcLayer = orcaRpcLayerForRequest(token);
    // oxlint-disable-next-line eslint-js/no-restricted-syntax -- Effect.runPromise requires await
    const detail = await Effect.runPromise(
      RpcClient.make(AdminOrcaTaskRpcs).pipe(
        Effect.flatMap((client) => client.admin["orcaTask.detail"]({ issueIdentifier })),
        Effect.provide(rpcLayer),
        Effect.scoped,
        Effect.catchAll(() => Effect.succeed(null as typeof OrcaTaskDetail.Type | null)),
      ),
    );
    if (detail === null) return { dehydratedAtoms: [] };
    // oxlint-disable-next-line eslint-js/no-restricted-syntax -- hydrateAtoms returns a Promise
    const hydration = await hydrateAtoms({
      [orcaTaskDetailKey(issueIdentifier)]: {
        schema: OrcaTaskDetail,
        effect: Effect.succeed(detail),
      },
    })(ctx);
    return { dehydratedAtoms: hydration.dehydratedAtoms };
  });

/**
 * SSR read of `loops.viewer.invocation` for the invocation page (OL-258).
 *
 * The page's live atom is a STREAM, which cannot run on the server, so the
 * first paint comes from this one-shot read dehydrated under
 * {@link loopInvocationKey}. A failed read (no session, no such invocation)
 * dehydrates nothing and the page falls through to its own loading frame —
 * never an SSR crash.
 *
 * The visitor's brand host rides along as `X-Forwarded-Host`: the rpc's
 * `LoopActorMiddleware` picks the Clerk instance from it, and the internal hop's
 * own `Host` is a service name that resolves to the default brand — which fails
 * every orcaloops.ai session, drops the seed, and makes the page hydrate a
 * server-rendered "not found" under a client-side success (React #418).
 */
const loopInvocationServerFn = createServerFn({ method: "GET" })
  .inputValidator((invocationId: string): string => invocationId)
  // oxlint-disable-next-line eslint-js/no-restricted-syntax -- TanStack Start server functions require async/await
  .handler(async (ctx): Promise<DehydrationPayload> => {
    const invocationId = ctx.data;
    if (!invocationId) return { dehydratedAtoms: [] };
    const token = getServerToken();
    if (!token) return { dehydratedAtoms: [] };
    const rpcLayer = orcaRpcLayerForRequest(token);
    // oxlint-disable-next-line eslint-js/no-restricted-syntax -- Effect.runPromise requires await
    const detail = await Effect.runPromise(
      RpcClient.make(LoopViewerRpcs).pipe(
        Effect.flatMap((client) => client.loops["viewer.invocation"]({ invocationId })),
        Effect.provide(rpcLayer),
        Effect.scoped,
        Effect.catchAll(() => Effect.succeed(null as typeof LoopInvocationView.Type | null)),
      ),
    );
    if (detail === null) return { dehydratedAtoms: [] };
    // oxlint-disable-next-line eslint-js/no-restricted-syntax -- hydrateAtoms returns a Promise
    const hydration = await hydrateAtoms({
      [loopInvocationKey(invocationId)]: {
        schema: LoopInvocationView,
        effect: Effect.succeed(detail),
      },
    })(ctx);
    return { dehydratedAtoms: hydration.dehydratedAtoms };
  });

/**
 * SSR read of `loops.drafts.get` for the live draft canvas (OL-314).
 *
 * The page's live atom is the `loops.drafts.watch` STREAM, which cannot run on
 * the server, so the first paint comes from this one-shot read dehydrated under
 * {@link loopDraftKey}. A failed read (no session, no such draft) dehydrates
 * nothing and the page falls through to its own loading frame — never an SSR
 * crash.
 *
 * The visitor's brand host rides along as `X-Forwarded-Host` for exactly the
 * reason `loopInvocationServerFn` documents: `LoopActorMiddleware` picks the
 * Clerk instance from it, and the internal hop's own `Host` is a service name
 * that resolves to the default brand — which fails every orcaloops.ai session,
 * drops the seed, and makes the page hydrate a server-rendered "not found"
 * under a client-side success (React #418).
 */
const loopDraftServerFn = createServerFn({ method: "GET" })
  .inputValidator((draftId: string): string => draftId)
  // oxlint-disable-next-line eslint-js/no-restricted-syntax -- TanStack Start server functions require async/await
  .handler(async (ctx): Promise<DehydrationPayload> => {
    const draftId = ctx.data;
    if (!draftId) return { dehydratedAtoms: [] };
    const token = getServerToken();
    if (!token) return { dehydratedAtoms: [] };
    const rpcLayer = orcaRpcLayerForRequest(token);
    // oxlint-disable-next-line eslint-js/no-restricted-syntax -- Effect.runPromise requires await
    const detail = await Effect.runPromise(
      RpcClient.make(LoopDraftRpcs).pipe(
        Effect.flatMap((client) => client.loops["drafts.get"]({ draftId })),
        Effect.provide(rpcLayer),
        Effect.scoped,
        Effect.catchAll(() => Effect.succeed(null as typeof LoopDraftView.Type | null)),
      ),
    );
    if (detail === null) return { dehydratedAtoms: [] };
    // oxlint-disable-next-line eslint-js/no-restricted-syntax -- hydrateAtoms returns a Promise
    const hydration = await hydrateAtoms({
      [loopDraftKey(draftId)]: {
        schema: LoopDraftView,
        effect: Effect.succeed(detail),
      },
    })(ctx);
    return { dehydratedAtoms: hydration.dehydratedAtoms };
  });

// ============================================================================
// beforeLoad wrappers — thin client-side bridges that merge the server payload
// into the parent route context.
// ============================================================================

const mergeAtoms = (
  parent: Array<Hydration.DehydratedAtom> | undefined,
  next: Array<Hydration.DehydratedAtom>,
): Array<Hydration.DehydratedAtom> => [...(parent ?? []), ...next];

/**
 * Server-side `beforeLoad` for `/orca/` — dehydrates the orca task list.
 *
 * @since 1.0.0
 */
// oxlint-disable-next-line eslint-js/no-restricted-syntax -- TanStack Start beforeLoad hooks require async/await
export const orcaTasksBeforeLoad = async (args: BeforeLoadArgs): Promise<BeforeLoadResult> => {
  // oxlint-disable-next-line eslint-js/no-restricted-syntax -- Server function returns Promise
  const result = await orcaTasksServerFn();
  return { dehydratedAtoms: mergeAtoms(args.context?.dehydratedAtoms, result.dehydratedAtoms) };
};

/**
 * Server-side `beforeLoad` for `/orca/projects/` — dehydrates the projects list.
 *
 * @since 1.0.0
 */
// oxlint-disable-next-line eslint-js/no-restricted-syntax -- TanStack Start beforeLoad hooks require async/await
export const orcaProjectsBeforeLoad = async (args: BeforeLoadArgs): Promise<BeforeLoadResult> => {
  // oxlint-disable-next-line eslint-js/no-restricted-syntax -- Server function returns Promise
  const result = await orcaProjectsServerFn();
  return { dehydratedAtoms: mergeAtoms(args.context?.dehydratedAtoms, result.dehydratedAtoms) };
};

/**
 * Server-side `beforeLoad` for `/orca/projects/$projectId` — dehydrates the
 * per-project detail.
 *
 * @since 1.0.0
 */
// oxlint-disable-next-line eslint-js/no-restricted-syntax -- TanStack Start beforeLoad hooks require async/await
export const orcaProjectDetailBeforeLoad = async (args: BeforeLoadArgs): Promise<BeforeLoadResult> => {
  const projectId = args.params?.projectId;
  if (!projectId) {
    return { dehydratedAtoms: args.context?.dehydratedAtoms ?? [] };
  }
  // oxlint-disable-next-line eslint-js/no-restricted-syntax -- Server function returns Promise
  const result = await orcaProjectDetailServerFn({ data: projectId });
  return { dehydratedAtoms: mergeAtoms(args.context?.dehydratedAtoms, result.dehydratedAtoms) };
};

/**
 * Server-side `beforeLoad` for
 * `/orca/loops/$loopId/invocations/$invocationId` — dehydrates the invocation
 * view so the page's first paint is the walk, not a spinner (OL-258).
 *
 * @since 1.0.0
 */
// oxlint-disable-next-line eslint-js/no-restricted-syntax -- TanStack Start beforeLoad hooks require async/await
export const loopInvocationBeforeLoad = async (args: BeforeLoadArgs): Promise<BeforeLoadResult> => {
  const invocationId = args.params?.invocationId;
  if (!invocationId) {
    return { dehydratedAtoms: args.context?.dehydratedAtoms ?? [] };
  }
  // oxlint-disable-next-line eslint-js/no-restricted-syntax -- Server function returns Promise
  const result = await loopInvocationServerFn({ data: invocationId });
  return { dehydratedAtoms: mergeAtoms(args.context?.dehydratedAtoms, result.dehydratedAtoms) };
};

/**
 * Server-side `beforeLoad` for the live draft canvas — dehydrates the draft so
 * the page's first paint is the document, not a spinner (OL-314).
 *
 * @since 1.0.0
 */
// oxlint-disable-next-line eslint-js/no-restricted-syntax -- TanStack Start beforeLoad hooks require async/await
export const loopDraftBeforeLoad = async (args: BeforeLoadArgs): Promise<BeforeLoadResult> => {
  const draftId = args.params?.draftId;
  if (!draftId) {
    return { dehydratedAtoms: args.context?.dehydratedAtoms ?? [] };
  }
  // oxlint-disable-next-line eslint-js/no-restricted-syntax -- Server function returns Promise
  const result = await loopDraftServerFn({ data: draftId });
  return { dehydratedAtoms: mergeAtoms(args.context?.dehydratedAtoms, result.dehydratedAtoms) };
};

/**
 * Server-side `beforeLoad` for `/orca/runners` — dehydrates the runner pool.
 *
 * @since 1.0.0
 */
// oxlint-disable-next-line eslint-js/no-restricted-syntax -- TanStack Start beforeLoad hooks require async/await
export const orcaRunnersBeforeLoad = async (args: BeforeLoadArgs): Promise<BeforeLoadResult> => {
  // oxlint-disable-next-line eslint-js/no-restricted-syntax -- Server function returns Promise
  const result = await orcaRunnersServerFn();
  return { dehydratedAtoms: mergeAtoms(args.context?.dehydratedAtoms, result.dehydratedAtoms) };
};

/**
 * Server-side `beforeLoad` for `/orca/events` — dehydrates the 24h events
 * summary.
 *
 * @since 1.0.0
 */
// oxlint-disable-next-line eslint-js/no-restricted-syntax -- TanStack Start beforeLoad hooks require async/await
export const orcaEventsBeforeLoad = async (args: BeforeLoadArgs): Promise<BeforeLoadResult> => {
  // oxlint-disable-next-line eslint-js/no-restricted-syntax -- Server function returns Promise
  const result = await orcaEventsServerFn();
  return { dehydratedAtoms: mergeAtoms(args.context?.dehydratedAtoms, result.dehydratedAtoms) };
};

/**
 * Server-side `beforeLoad` for `/orca/defaults` — dehydrates the platform
 * defaults snapshot.
 *
 * @since 1.0.0
 */
// oxlint-disable-next-line eslint-js/no-restricted-syntax -- TanStack Start beforeLoad hooks require async/await
export const orcaDefaultsBeforeLoad = async (args: BeforeLoadArgs): Promise<BeforeLoadResult> => {
  // oxlint-disable-next-line eslint-js/no-restricted-syntax -- Server function returns Promise
  const result = await orcaDefaultsServerFn();
  return { dehydratedAtoms: mergeAtoms(args.context?.dehydratedAtoms, result.dehydratedAtoms) };
};

/**
 * Server-side `beforeLoad` for `/orca/repos` — dehydrates the registry list and
 * the seen-repos list.
 *
 * @since 1.0.0
 */
// oxlint-disable-next-line eslint-js/no-restricted-syntax -- TanStack Start beforeLoad hooks require async/await
export const orcaReposBeforeLoad = async (args: BeforeLoadArgs): Promise<BeforeLoadResult> => {
  // oxlint-disable-next-line eslint-js/no-restricted-syntax -- Server function returns Promise
  const result = await orcaReposServerFn();
  return { dehydratedAtoms: mergeAtoms(args.context?.dehydratedAtoms, result.dehydratedAtoms) };
};

/**
 * Server-side `beforeLoad` for `/orca/repos/$owner/$name` — dehydrates the
 * per-repo detail. Bails to the parent atoms when either param is absent; a
 * `RecordNotFoundError` (unconfigured repo) yields no dehydrated atom.
 *
 * @since 1.0.0
 */
// oxlint-disable-next-line eslint-js/no-restricted-syntax -- TanStack Start beforeLoad hooks require async/await
export const orcaRepoDetailBeforeLoad = async (args: BeforeLoadArgs): Promise<BeforeLoadResult> => {
  const owner = args.params?.owner;
  const name = args.params?.name;
  if (!owner || !name) {
    return { dehydratedAtoms: args.context?.dehydratedAtoms ?? [] };
  }
  // oxlint-disable-next-line eslint-js/no-restricted-syntax -- Server function returns Promise
  const result = await orcaRepoDetailServerFn({ data: `${owner}/${name}` });
  return { dehydratedAtoms: mergeAtoms(args.context?.dehydratedAtoms, result.dehydratedAtoms) };
};

/**
 * Server-side `beforeLoad` for `/orca/task/$issueIdentifier` — dehydrates the
 * identifier-keyed task-detail read model.
 *
 * @since 1.0.0
 */
// oxlint-disable-next-line eslint-js/no-restricted-syntax -- TanStack Start beforeLoad hooks require async/await
export const orcaTaskDetailBeforeLoad = async (args: BeforeLoadArgs): Promise<BeforeLoadResult> => {
  const issueIdentifier = args.params?.issueIdentifier;
  if (!issueIdentifier) {
    return { dehydratedAtoms: args.context?.dehydratedAtoms ?? [] };
  }
  // oxlint-disable-next-line eslint-js/no-restricted-syntax -- Server function returns Promise
  const result = await orcaTaskDetailServerFn({ data: issueIdentifier });
  return { dehydratedAtoms: mergeAtoms(args.context?.dehydratedAtoms, result.dehydratedAtoms) };
};
